import { TabGroup, TabGroupTitle, TabList, Tab } from "@utahdts/utah-design-system";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import WaterSystemSummaryTable from './WaterSystemSummaryTable';
import WaterMonitoringPage from './WaterMonitoringPage';
import DownloadWaterSystemReports from './DownloadWaterSystemReports';
import { useWaterSystemSearchModal } from "../reuseableComponents/WaterSystemSearchModalContext";
import { GET_WATER_SYSTEM_HOME_DETAIL_AND_REPORT_URLS } from "../../queries/menuQrys";

const TAB_CONFIG = [
  { id: 'water-monitoring', label: 'Water Monitoring' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'population-connections', label: 'Population/Connections' },
  { id: 'bacterial-summary', label: 'Bacterial Summary' },
  { id: 'ips', label: 'IPS' },
  { id: 'detailed-info', label: 'Detailed Info' }
];

const DEFAULT_TAB_ID = TAB_CONFIG[0].id;
const LOCAL_TAB_IDS = new Set(['detailed-info', 'water-monitoring']);
const VALID_TAB_IDS = new Set(TAB_CONFIG.map((tab) => tab.id));

const getTabIdFromSearch = (search, visibleTabs = TAB_CONFIG) => {
  const tabId = new URLSearchParams(search).get('tab');
  if (VALID_TAB_IDS.has(tabId)) {
    return tabId;
  }

  return visibleTabs.some((tab) => tab.id === tabId) ? tabId : (visibleTabs[0]?.id || DEFAULT_TAB_ID);
};

const getReportTabId = (reportItem) => {
  const route = reportItem?.url_route || '';
  const queryMatch = route.match(/(?:\?|&)tab=([^&]+)/i);
  if (queryMatch?.[1]) {
    const tabId = decodeURIComponent(queryMatch[1]);
    if (VALID_TAB_IDS.has(tabId)) {
      return tabId;
    }
  }

  const tabByLabel = TAB_CONFIG.find((tab) => tab.label === reportItem?.label);
  return tabByLabel?.id;
};

const WaterSystemHome = () => {
  const history = useHistory();
  const location = useLocation();
  const { selectedWaterSystem: contextWaterSystem, isRestoringFromCookie } = useWaterSystemSearchModal();
  const selectedWaterSystem = contextWaterSystem || JSON.parse(sessionStorage.getItem("selectedWaterSystem") || "{}");

  //selectedWaterSystem inside of a handler on this page can still drift.
  const latestSelectedWaterSystemRef = useRef(selectedWaterSystem);
  useEffect(() => {
    latestSelectedWaterSystemRef.current = selectedWaterSystem;
  }, [selectedWaterSystem]);

  const [waterSystemHomeDetailUrls, setWaterSystemHomeDetailUrls] = useState([]);
  const [waterSystemHomeReportUrls, setWaterSystemHomeReportUrls] = useState([]);
  const [reportRouteByTabId, setReportRouteByTabId] = useState(() => new Map());
  const { data: waterSystemHomeUrlsData } = useQuery(GET_WATER_SYSTEM_HOME_DETAIL_AND_REPORT_URLS, {
    variables: { pwsId: selectedWaterSystem?.pwsId },
    skip: !selectedWaterSystem?.pwsId,
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    const payload = waterSystemHomeUrlsData?.getWaterSystemHomeDetailAndReportUrls;
    if (!payload) {
      return;
    }

    const detailUrls = payload.waterSystemHomeDetailUrls || [];
    const reportUrls = payload.waterSystemHomeReportUrls || [];

    setWaterSystemHomeDetailUrls(detailUrls);
    setWaterSystemHomeReportUrls(reportUrls);

    const map = new Map();
    reportUrls.forEach((item) => {
      const tabId = getReportTabId(item);
      if (tabId && item?.url_route) {
        map.set(tabId, item.url_route);
      }
    });
    setReportRouteByTabId(map);
  }, [waterSystemHomeUrlsData]);

  const visibleTabs = useMemo(() => {
    if (!waterSystemHomeReportUrls.length) {
      return [];
    }

    const visibleIds = new Set([DEFAULT_TAB_ID, 'water-monitoring', 'detailed-info']);
    waterSystemHomeReportUrls.forEach((item) => {
      const tabId = getReportTabId(item);
      if (tabId) {
        visibleIds.add(tabId);
      }
    });

    if (!visibleIds.size) {
      return [];
    }

    return TAB_CONFIG.filter((tab) => visibleIds.has(tab.id));
  }, [waterSystemHomeReportUrls]);

  const activeTab = useMemo(
    () => getTabIdFromSearch(location.search, visibleTabs),
    [location.search, visibleTabs]
  );

  useEffect(() => {
    // If selectedWaterSystem is not populated, redirect to home
    if ((!selectedWaterSystem || Object.keys(selectedWaterSystem).length === 0) && !isRestoringFromCookie) {
      history.push("/");
    }
  }, [selectedWaterSystem, history, isRestoringFromCookie]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const selectedPwsId = selectedWaterSystem?.pwsId ? String(selectedWaterSystem.pwsId) : '';
    const shouldUpdateTab = searchParams.get('tab') !== activeTab;
    const shouldUpdatePwsId = selectedPwsId
      ? searchParams.get('pwsId') !== selectedPwsId
      : searchParams.has('pwsId');

    if (shouldUpdateTab || shouldUpdatePwsId) {
      searchParams.set('tab', activeTab);
      if (selectedPwsId) {
        searchParams.set('pwsId', selectedPwsId);
      } else {
        searchParams.delete('pwsId');
      }
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : ''
      });
    }
  }, [activeTab, history, location.pathname, location.search, selectedWaterSystem]);

  const handleTabChange = (tabId) => {
    const latestSelectedWaterSystem = latestSelectedWaterSystemRef.current || {};

    if (LOCAL_TAB_IDS.has(tabId)) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', tabId);
      if (latestSelectedWaterSystem?.pwsId) {
        searchParams.set('pwsId', latestSelectedWaterSystem.pwsId);
      }
      history.replace({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`
      });
      return;
    }

    const routeTemplate = reportRouteByTabId.get(tabId);
    if (!routeTemplate) {
      return;
    }

    const resolvedRoute = routeTemplate
      .replace(/%systemId%/g, encodeURIComponent(latestSelectedWaterSystem?.systemId || ''));

    window.location.assign(resolvedRoute);
  };

  return (
    <div className="page-container water-monitoring-page">
      <div className="water-system-home-header-row">
        <h1 className="water-system-home-header-title">Water System Home</h1>
        <div className="water-system-home-header-actions">
          {activeTab !== 'detailed-info' && (
            <DownloadWaterSystemReports
              activeTab={activeTab}
              selectedWaterSystem={selectedWaterSystem}
            />
          )}
        </div>
      </div>
        <TabGroup className="utah-design-system " value={activeTab} onChange={handleTabChange}>
          <TabGroupTitle className="visually-hidden">Water System Home Navigation</TabGroupTitle>
          <TabList>
            {visibleTabs.map((tab) => (
              <Tab key={tab.id} id={tab.id}>{tab.label}</Tab>
            ))}
          </TabList>
        </TabGroup>
        <br/>
      {selectedWaterSystem && Object.keys(selectedWaterSystem).length > 0 && (
        <WaterSystemSummaryTable
          selectedWaterSystem={selectedWaterSystem}
          waterSystemHomeDetailUrls={waterSystemHomeDetailUrls}
        />
      )}
      <div>

        <div className="mt-4">
          {visibleTabs.map((tab) => (
            <div
              key={tab.id}
              role="tabpanel"
              id={`tabpanel-${tab.id}`}
              aria-labelledby={tab.id}
              hidden={activeTab !== tab.id}
            >
              {tab.id === 'water-monitoring' ? <WaterMonitoringPage isEmbedded /> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export default WaterSystemHome;
